/**
 * Holded integration tab inside Accounting page.
 * Lets the admin:
 *   • Configure API key & toggle auto-sync flags
 *   • Test the connection
 *   • Pull contacts from Holded → ZENTRA clients
 *   • Import historical invoices from Holded → ZENTRA invoices
 *   • Trigger payment status sync manually (also runs every 15 min in background)
 */
import { useEffect, useState, useCallback } from "react";
import {
    CheckCircle2,
    XCircle,
    RefreshCw,
    Download,
    Users,
    Settings as SettingsIcon,
    Plug,
    AlertCircle,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function HoldedTab() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [error, setError] = useState("");
    const [busyAction, setBusyAction] = useState("");
    const [actionResult, setActionResult] = useState(null);

    // Form state
    const [apiKey, setApiKey] = useState("");
    const [enabled, setEnabled] = useState(false);
    const [autoSyncPayments, setAutoSyncPayments] = useState(true);
    const [autoPushInvoices, setAutoPushInvoices] = useState(false);
    const [autoPushExpenses, setAutoPushExpenses] = useState(false);

    // Import range
    const today = new Date().toISOString().slice(0, 10);
    const firstOfYear = `${new Date().getFullYear()}-01-01`;
    const [fromDate, setFromDate] = useState(firstOfYear);
    const [toDate, setToDate] = useState(today);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/holded/settings");
            setSettings(data);
            setEnabled(data.enabled ?? false);
            setAutoSyncPayments(data.auto_sync_payments ?? true);
            setAutoPushInvoices(data.auto_push_invoices ?? false);
            setAutoPushExpenses(data.auto_push_expenses ?? false);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const save = async () => {
        setSaving(true);
        setError("");
        try {
            const payload = {
                enabled,
                auto_sync_payments: autoSyncPayments,
                auto_push_invoices: autoPushInvoices,
                auto_push_expenses: autoPushExpenses,
            };
            if (apiKey && apiKey.trim()) payload.api_key = apiKey.trim();
            const { data } = await api.post("/holded/settings", payload);
            setSettings(data);
            setApiKey("");
            setActionResult({ type: "save", message: "Configuración guardada." });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const testConnection = async () => {
        setTesting(true);
        setTestResult(null);
        setError("");
        try {
            const { data } = await api.post("/holded/test-connection");
            setTestResult({ ok: true, ...data });
        } catch (err) {
            setTestResult({ ok: false, error: formatApiError(err) });
        } finally {
            setTesting(false);
        }
    };

    const runAction = async (label, fn) => {
        setBusyAction(label);
        setActionResult(null);
        setError("");
        try {
            const result = await fn();
            setActionResult({ type: label, ...result });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusyAction("");
        }
    };

    if (loading) return <div className="zentra-skeleton h-80" />;

    return (
        <div className="space-y-6" data-testid="holded-tab">
            {/* Status header */}
            <div className="bg-white border border-zentra-border rounded-lg p-5">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
                            <Plug size={20} />
                        </div>
                        <div>
                            <h3 className="text-base font-semibold text-zentra-ink">Holded</h3>
                            <p className="text-xs text-zentra-muted mt-0.5">
                                Plataforma de facturación y contabilidad. Sincronización bidireccional.
                            </p>
                            <div className="mt-3 flex items-center gap-2 flex-wrap">
                                {settings?.has_api_key ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                        <CheckCircle2 size={12} /> API key configurada
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                        <AlertCircle size={12} /> Sin API key
                                    </span>
                                )}
                                <span
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                                        settings?.enabled
                                            ? "bg-blue-50 text-blue-700 border-blue-200"
                                            : "bg-zinc-100 text-zinc-600 border-zinc-200"
                                    }`}
                                >
                                    {settings?.enabled ? "Activado" : "Inactivo"}
                                </span>
                                {settings?.last_payment_sync_at && (
                                    <span className="text-[11px] text-zentra-muted">
                                        Última sync pagos: {new Date(settings.last_payment_sync_at).toLocaleString("es-ES")}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={testConnection}
                        disabled={testing || !settings?.has_api_key}
                        className="zentra-btn-secondary text-xs disabled:opacity-40"
                        data-testid="holded-test-connection-btn"
                    >
                        <RefreshCw size={13} className={testing ? "animate-spin" : ""} />
                        {testing ? "Probando…" : "Probar conexión"}
                    </button>
                </div>
                {testResult && (
                    <div
                        className={`mt-3 px-3 py-2 rounded-md text-xs ${
                            testResult.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"
                        }`}
                        data-testid="holded-test-result"
                    >
                        {testResult.ok ? (
                            <span className="inline-flex items-center gap-2">
                                <CheckCircle2 size={14} /> Conexión OK · {testResult.contacts_count} contactos en Holded
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-2">
                                <XCircle size={14} /> {testResult.error}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md text-sm" data-testid="holded-error">
                    {error}
                </div>
            )}

            {actionResult && (
                <div
                    className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-md text-sm"
                    data-testid="holded-action-result"
                >
                    <strong className="block mb-0.5">Acción completada: {actionResult.type}</strong>
                    {actionResult.created !== undefined && (
                        <span>
                            Creados: <b>{actionResult.created}</b> · Actualizados: <b>{actionResult.updated || 0}</b>
                            {actionResult.skipped !== undefined ? ` · Omitidos: ${actionResult.skipped}` : ""}
                            {actionResult.total_holded !== undefined ? ` · Total en Holded: ${actionResult.total_holded}` : ""}
                        </span>
                    )}
                    {actionResult.marked_paid !== undefined && (
                        <span>
                            Facturas marcadas como pagadas: <b>{actionResult.marked_paid}</b> · Comprobadas:{" "}
                            {actionResult.pending_checked}
                        </span>
                    )}
                    {actionResult.message && <span>{actionResult.message}</span>}
                </div>
            )}

            {/* Settings form */}
            <div className="bg-white border border-zentra-border rounded-lg p-5">
                <div className="flex items-center gap-2 mb-4">
                    <SettingsIcon size={16} className="text-zentra-accent-strong" />
                    <h4 className="text-sm font-semibold text-zentra-ink">Configuración</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="space-y-1 md:col-span-2">
                        <span className="text-xs font-medium text-zentra-muted">API Key de Holded</span>
                        <input
                            type="password"
                            value={apiKey}
                            onChange={(e) => setApiKey(e.target.value)}
                            placeholder={settings?.has_api_key ? "•••••••••• (cambiar)" : "Pega tu API key aquí"}
                            className="zentra-input w-full"
                            data-testid="holded-apikey-input"
                            autoComplete="off"
                        />
                        <span className="text-[11px] text-zentra-muted">
                            Se guarda encriptada en la base de datos. Obtén la tuya en{" "}
                            <a
                                href="https://app.holded.com/settings/developers"
                                target="_blank"
                                rel="noreferrer"
                                className="text-zentra-accent-strong hover:underline"
                            >
                                Holded → Ajustes → Desarrolladores
                            </a>
                        </span>
                    </label>

                    <ToggleRow
                        label="Integración activada"
                        hint="Todas las operaciones con Holded quedan habilitadas."
                        checked={enabled}
                        onChange={setEnabled}
                        testId="holded-toggle-enabled"
                    />
                    <ToggleRow
                        label="Auto-sync de pagos"
                        hint="Cada 15 min revisa pagos en Holded y los marca en ZENTRA."
                        checked={autoSyncPayments}
                        onChange={setAutoSyncPayments}
                        testId="holded-toggle-auto-payments"
                    />
                    <ToggleRow
                        label="Auto-push de facturas"
                        hint="Crear factura en ZENTRA → la replica automáticamente en Holded."
                        checked={autoPushInvoices}
                        onChange={setAutoPushInvoices}
                        testId="holded-toggle-auto-invoices"
                    />
                    <ToggleRow
                        label="Auto-push de gastos"
                        hint="Crear gasto en ZENTRA → lo replica como compra en Holded."
                        checked={autoPushExpenses}
                        onChange={setAutoPushExpenses}
                        testId="holded-toggle-auto-expenses"
                    />
                </div>

                <div className="mt-4 flex justify-end">
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving}
                        className="zentra-btn-primary text-sm"
                        data-testid="holded-save-btn"
                    >
                        {saving ? "Guardando…" : "Guardar configuración"}
                    </button>
                </div>
            </div>

            {/* Manual sync actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SyncCard
                    icon={Users}
                    title="Importar contactos"
                    description="Trae todos los clientes de Holded a ZENTRA (vinculados por CIF/NIF)."
                    actionLabel="Importar contactos"
                    busy={busyAction === "pull-contacts"}
                    disabled={!settings?.has_api_key || !settings?.enabled}
                    onClick={() =>
                        runAction("pull-contacts", async () => {
                            const { data } = await api.post("/holded/sync/contacts/pull", { create_missing: true });
                            return data;
                        })
                    }
                    testId="holded-pull-contacts-card"
                />

                <SyncCard
                    icon={RefreshCw}
                    title="Sincronizar estado de pagos"
                    description="Comprueba en Holded qué facturas están cobradas y las marca en ZENTRA."
                    actionLabel="Sincronizar pagos"
                    busy={busyAction === "sync-payments"}
                    disabled={!settings?.has_api_key || !settings?.enabled}
                    onClick={() =>
                        runAction("sync-payments", async () => {
                            const { data } = await api.post("/holded/sync/payments");
                            return data;
                        })
                    }
                    testId="holded-sync-payments-card"
                />
            </div>

            {/* Import invoices with date range */}
            <div className="bg-white border border-zentra-border rounded-lg p-5">
                <div className="flex items-center gap-2 mb-3">
                    <Download size={16} className="text-zentra-accent-strong" />
                    <h4 className="text-sm font-semibold text-zentra-ink">Importar facturas de Holded</h4>
                </div>
                <p className="text-xs text-zentra-muted mb-4">
                    Trae las facturas emitidas en Holded entre las fechas seleccionadas. Las que ya existen en ZENTRA se actualizan;
                    las nuevas se crean enlazadas a su contacto correspondiente.
                </p>
                <div className="flex items-end gap-3 flex-wrap">
                    <label className="space-y-1">
                        <span className="text-[11px] font-medium text-zentra-muted">Desde</span>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="zentra-input"
                            data-testid="holded-import-from"
                        />
                    </label>
                    <label className="space-y-1">
                        <span className="text-[11px] font-medium text-zentra-muted">Hasta</span>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="zentra-input"
                            data-testid="holded-import-to"
                        />
                    </label>
                    <button
                        type="button"
                        onClick={() =>
                            runAction("import-invoices", async () => {
                                const { data } = await api.post("/holded/sync/invoices/import", {
                                    from_date: fromDate,
                                    to_date: toDate,
                                    create_missing_clients: true,
                                });
                                return data;
                            })
                        }
                        disabled={busyAction === "import-invoices" || !settings?.has_api_key || !settings?.enabled}
                        className="zentra-btn-primary text-sm"
                        data-testid="holded-import-invoices-btn"
                    >
                        {busyAction === "import-invoices" ? "Importando…" : "Importar facturas"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ToggleRow({ label, hint, checked, onChange, testId }) {
    return (
        <label className="flex items-start gap-3 p-3 border border-zentra-border rounded-md cursor-pointer hover:bg-zentra-bg/50 transition">
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="mt-0.5 accent-emerald-600"
                data-testid={testId}
            />
            <span className="flex-1">
                <span className="block text-sm font-medium text-zentra-ink">{label}</span>
                <span className="block text-[11px] text-zentra-muted mt-0.5">{hint}</span>
            </span>
        </label>
    );
}

function SyncCard({ icon: Icon, title, description, actionLabel, busy, disabled, onClick, testId }) {
    return (
        <div className="bg-white border border-zentra-border rounded-lg p-5" data-testid={testId}>
            <div className="flex items-start gap-3 mb-3">
                <div className="w-9 h-9 rounded-md bg-zentra-bg flex items-center justify-center text-zentra-accent-strong">
                    <Icon size={16} />
                </div>
                <div className="flex-1">
                    <h5 className="text-sm font-semibold text-zentra-ink">{title}</h5>
                    <p className="text-[11px] text-zentra-muted mt-0.5">{description}</p>
                </div>
            </div>
            <button
                type="button"
                onClick={onClick}
                disabled={busy || disabled}
                className="zentra-btn-secondary text-xs w-full justify-center"
                data-testid={`${testId}-btn`}
            >
                {busy ? "Procesando…" : actionLabel}
            </button>
        </div>
    );
}
